// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiClient from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setOrganization(null);
        delete apiClient.defaults.headers.common['Authorization'];
    }, []);

    const fetchUserAndOrg = useCallback(async () => {
        if (token) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            try {
                const userResponse = await apiClient.get('/users/me/');
                const currentUser = userResponse.data;
                setUser(currentUser);

                if (currentUser.organization_id) {
                    const orgResponse = await apiClient.get(`/organizations/${currentUser.organization_id}`);
                    setOrganization(orgResponse.data);
                } else {
                    setOrganization(null);
                }
            } catch (error) {
                logout();
            }
        }
    }, [token, logout]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            if (token) {
                await fetchUserAndOrg();
            }
            setLoading(false);
        }
        load();
    }, [token, fetchUserAndOrg]);

    const reloadUser = useCallback(async () => {
        setLoading(true);
        await fetchUserAndOrg();
        setLoading(false);
    }, [fetchUserAndOrg]);

    const login = async (email, password) => {
        const params = new URLSearchParams();
        params.append('username', email);
        params.append('password', password);

        const response = await apiClient.post('/token', params, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        
        const { access_token, user } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(user);

        if (user.organization_id) {
            const orgResponse = await apiClient.get(`/organizations/${user.organization_id}`);
            setOrganization(orgResponse.data);
        }
        
        return user;
    };

    const updateUser = (updatedUserData) => {
        setUser(updatedUserData);
    };

    const value = {
        user,
        token,
        organization,
        setOrganization,
        login,
        logout,
        updateUser,
        isAuthenticated: !!token,
        reloadUser,
    };

    if (loading) {
        return <div>Загрузка...</div>;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};